// src/pages/Courses.jsx
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import coursesData from '../data/courses.json';
import categoriesData from '../data/courseCategories.json';

const Courses = () => {
  const { category } = useParams();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [freeFilter, setFreeFilter] = useState('all');

  // Update categories with counts
  const categoriesWithCounts = categoriesData.map(cat => ({
    ...cat,
    count: coursesData.filter(c => c.category === cat.name).length
  }));

  useEffect(() => {
    let filtered = [...coursesData];
    
    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(term) ||
        course.description.toLowerCase().includes(term) ||
        course.skills.some(skill => skill.toLowerCase().includes(term))
      );
    }
    
    // Platform filter
    if (platformFilter !== 'all') {
      filtered = filtered.filter(course => course.platform === platformFilter);
    }
    
    // Level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(course => course.level === levelFilter);
    }
    
    // Free filter
    if (freeFilter !== 'all') {
      const isFree = freeFilter === 'free';
      filtered = filtered.filter(course => course.free === isFree);
    }
    
    setCourses(coursesData);
    setFilteredCourses(filtered);
  }, [selectedCategory, searchTerm, platformFilter, levelFilter, freeFilter]);

  // Get unique platforms and levels for filters
  const platforms = [...new Set(coursesData.map(c => c.platform))];
  const levels = [...new Set(coursesData.map(c => c.level))];

  return (
    <section className="full-section courses-page">

      {/* Filters */}
      <div className="courses-filters">
        <div className="filter-group">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <select 
              value={platformFilter} 
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
            
            <select 
              value={levelFilter} 
              onChange={(e) => setLevelFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Levels</option>
              {levels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
            
            <div className="filter-tags">
              <button
                className={`filter-tag ${freeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setFreeFilter('all')}
              >
                All Courses
              </button>
              <button
                className={`filter-tag ${freeFilter === 'free' ? 'active' : ''}`}
                onClick={() => setFreeFilter('free')}
              >
                🆓 Free Only
              </button>
              <button
                className={`filter-tag ${freeFilter === 'paid' ? 'active' : ''}`}
                onClick={() => setFreeFilter('paid')}
              >
                💰 Paid Only
              </button>
            </div>
          </div>
        </div>
        
        <div className="results-info">
          <p>Showing {filteredCourses.length} of {coursesData.length} courses</p>
          {selectedCategory !== 'all' && (
            <button 
              className="clear-filter"
              onClick={() => setSelectedCategory('all')}
            >
              Clear Category Filter
            </button>
          )}
        </div>
      </div>

      {/* Courses Grid */}
      <div className="courses-grid-full">
        {filteredCourses.length > 0 ? (
          filteredCourses.map(course => (
            <CourseCard key={course.id} course={course} />
          ))
        ) : (
          <div className="no-results">
            <h3>No courses found</h3>
            <p>Try adjusting your filters or search criteria</p>
            <button 
              className="btn-secondary"
              onClick={() => {
                setSelectedCategory('all');
                setSearchTerm('');
                setPlatformFilter('all');
                setLevelFilter('all');
                setFreeFilter('all');
              }}
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="courses-cta">
        <h2>Want personalized course recommendations?</h2>
        <p>Book a consultation to discuss your learning goals and career path</p>
        <button 
          className="btn-primary"
          onClick={() => window.open('https://calendly.com/ayoub-abdelouahabi/meeting', '_blank')}
        >
          Schedule Learning Consultation
        </button>
      </div>
    </section>
  );
};

// Course Card Component
const CourseCard = ({ course }) => {
  const getPlatformIcon = (platform) => {
    if (platform.includes('AWS')) return '☁️';
    if (platform.includes('Coursera')) return '🎓';
    if (platform.includes('Google')) return '🔍';
    if (platform.includes('Linux')) return '🐧';
    if (platform.includes('Educative')) return '📖';
    if (platform.includes('DeepLearning')) return '🧠';
    if (platform.includes('Stanford')) return '🎓';
    return '📚';
  };

  return (
    <div className={`course-card ${course.featured ? 'featured' : ''}`}>
      <div className="course-card-header">
        <div className="course-platform-icon">
          {getPlatformIcon(course.platform)}
        </div>
        <div className="course-badges">
          {course.free && <span className="badge free">🆓 Free</span>}
          {course.certificate && <span className="badge certificate">📜 Certificate</span>}
          {course.featured && <span className="badge featured">⭐ Featured</span>}
        </div>
      </div>
      
      <div className="course-card-content">
        <h3>{course.title}</h3>
        <p className="course-description">{course.description}</p>
        
        <div className="course-meta">
          <div className="meta-item">
            <span className="meta-label">Platform:</span>
            <span className="meta-value">{course.platform}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Duration:</span>
            <span className="meta-value">{course.duration}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Level:</span>
            <span className="meta-value">{course.level}</span>
          </div>
        </div>
        
        <div className="course-tags">
          <span className="course-category">{course.category}</span>
          {!course.free && <span className="course-price">{course.price}</span>}
        </div>
        
        <div className="course-skills">
          {course.skills.slice(0, 3).map(skill => (
            <span key={skill} className="skill-tag">{skill}</span>
          ))}
        </div>
      </div>
      
      <div className="course-card-footer">
        {course.source === 'local' ? (
          <Link to={`/courses/${course.id}`} className="btn-primary">
            Learn More
          </Link>
        ) : (
          <>
            <a 
              href={course.externalUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              View on {course.platform}
            </a>
            <Link to={`/courses/${course.id}`} className="btn-secondary">
              Course Details
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Courses;